/* Accounts: every call the app makes to Supabase Auth. Passwords are hashed, emails sent and
   tokens stored by Supabase and its client; nothing here, or anywhere else in the repo, does
   any of that by hand.

   Each call that a form makes resolves to an `AuthResult` rather than throwing: a wrong
   password is an answer to show, not a bug to report. */

import {
  isAuthRetryableFetchError,
  type AuthError,
  type SupabaseClient,
} from "@supabase/supabase-js";
import { createPwnedCheck, pwnedMessage, type PwnedCheck } from "./pwned";

/** The providers the app knows how to offer. Which ones a build shows is its own choice. */
export const OAUTH_PROVIDERS = ["google", "facebook"] as const;
export type OAuthProvider = (typeof OAUTH_PROVIDERS)[number];

/** Enforced by Supabase (config.toml locally, the Auth settings when hosted); checked in the
    form too, so the user hears about it before the request. */
export const MIN_PASSWORD_LENGTH = 12;

export interface AuthUser {
  id: string;
  email: string;
}

export type AuthResult<T = void> =
  { ok: true; value: T } | { ok: false; message: string; code: string };

export type AuthEvent = "signed-in" | "signed-out" | "recovery";

/** The stored session has run out and Supabase could not be reached to renew it. Not the same
    as signed out: the device may well be, it is just offline. */
export class AuthUnreachable extends Error {
  constructor() {
    super("Could not reach the server to check your session.");
    this.name = "AuthUnreachable";
  }
}

/** What the captcha handed over for one call, when the build has a captcha. Supabase checks
    it with the provider on the four calls a signed-out visitor can make, once captcha
    protection is on for the project; until then it is ignored. Single use. */
export type CaptchaToken = string | undefined;

/** `options` for supabase-js, which wants the key absent rather than undefined. */
const withCaptcha = (token: CaptchaToken): { captchaToken?: string } =>
  token ? { captchaToken: token } : {};

export interface AuthApi {
  /** Who is signed in on this device. Read from the stored session, so it answers offline
      for as long as the stored token lasts; past that it throws `AuthUnreachable`. */
  currentUser(): Promise<AuthUser | null>;
  /** Creates the account and sends the verification email. No session exists until the link
      in it is followed, so the value is only whether there is one already (confirmations
      off, as a local stack may be). */
  signUp(input: {
    email: string;
    password: string;
    displayName?: string;
    redirectTo: string;
    captchaToken?: CaptchaToken;
  }): Promise<AuthResult<{ signedIn: boolean }>>;
  signIn(
    email: string,
    password: string,
    captchaToken?: CaptchaToken,
  ): Promise<AuthResult<AuthUser>>;
  /** Leaves the page for the provider's; the result only comes back when that failed. */
  signInWith(provider: OAuthProvider, redirectTo: string): Promise<AuthResult>;
  resendVerification(
    email: string,
    redirectTo: string,
    captchaToken?: CaptchaToken,
  ): Promise<AuthResult>;
  /** Sends the reset link. Succeeds whether or not the address has an account. */
  sendPasswordReset(
    email: string,
    redirectTo: string,
    captchaToken?: CaptchaToken,
  ): Promise<AuthResult>;
  /** For a signed-in user, which someone who followed a reset link is. */
  setPassword(password: string): Promise<AuthResult>;
  signOut(): Promise<AuthResult>;
  /** Deletes the user and, by cascade, every row they own. Signs this device out. */
  deleteAccount(): Promise<AuthResult>;
  /** Returns the function that stops listening. */
  onChange(listener: (event: AuthEvent, user: AuthUser | null) => void): () => void;
}

/** A sentence a form can show. Supabase's own messages are fine English but speak of
    "credentials" and "rate limits"; the codes are stable, so the common ones are reworded
    and the rest pass through. */
export function authMessage(error: { code?: string | undefined; message: string }): string {
  switch (error.code) {
    case "invalid_credentials":
      return "That email and password do not match an account.";
    case "email_not_confirmed":
      return "This address is not verified yet. Follow the link in the email we sent you.";
    case "user_already_exists":
    case "email_exists":
      return "There is already an account for this address. Sign in, or reset its password.";
    case "weak_password":
      return `Choose a password of at least ${MIN_PASSWORD_LENGTH} characters.`;
    case "same_password":
      return "That is already your password. Choose a different one.";
    case "over_email_send_rate_limit":
    case "over_request_rate_limit":
      return "Too many attempts. Wait a minute, then try again.";
    case "email_address_invalid":
    case "validation_failed":
      return "That does not look like an email address.";
    case "signup_disabled":
      return "New accounts are not open at the moment.";
    case "provider_disabled":
      return "That sign-in method is not available yet.";
    case "captcha_failed":
      return "The check that you are a person did not go through. Wait for it to finish, then try again.";
    case "otp_expired":
      return "That link has expired or was already used. Ask for a new one.";
    default:
      return error.message || "Something went wrong. Try again.";
  }
}

const failed = (error: AuthError | { code?: string; message: string }): AuthResult<never> => ({
  ok: false,
  message: authMessage(error),
  code: error.code ?? "unknown",
});

const done: AuthResult = { ok: true, value: undefined };

const userOf = (u: { id: string; email?: string | undefined } | null | undefined) =>
  u ? ({ id: u.id, email: u.email ?? "" } satisfies AuthUser) : null;

/** A password known from a breach is refused before Supabase hears of it. Not knowing (the
    service is down, the device offline) refuses nothing: choosing a password must not depend
    on a third party being up, and Supabase's own length rule still stands. */
async function refusedAsPwned(
  check: PwnedCheck,
  password: string,
): Promise<Extract<AuthResult, { ok: false }> | null> {
  const seen = await check(password);
  return seen ? { ok: false, code: "pwned_password", message: pwnedMessage(seen) } : null;
}

export function createAuthApi(db: SupabaseClient, pwned: PwnedCheck = createPwnedCheck()): AuthApi {
  return {
    async currentUser() {
      const { data, error } = await db.auth.getSession();
      if (!data.session && error && isAuthRetryableFetchError(error)) throw new AuthUnreachable();
      return userOf(data.session?.user);
    },

    async signUp({ email, password, displayName, redirectTo, captchaToken }) {
      const refused = await refusedAsPwned(pwned, password);
      if (refused) return refused;
      const { data, error } = await db.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectTo,
          ...withCaptcha(captchaToken),
          // The profile trigger reads this, as it reads the name an OAuth provider sends.
          ...(displayName ? { data: { full_name: displayName } } : {}),
        },
      });
      if (error) return failed(error);
      return { ok: true, value: { signedIn: !!data.session } };
    },

    async signIn(email, password, captchaToken) {
      const { data, error } = await db.auth.signInWithPassword({
        email,
        password,
        options: withCaptcha(captchaToken),
      });
      if (error) return failed(error);
      return { ok: true, value: { id: data.user.id, email: data.user.email ?? email } };
    },

    async signInWith(provider, redirectTo) {
      const { error } = await db.auth.signInWithOAuth({ provider, options: { redirectTo } });
      return error ? failed(error) : done;
    },

    async resendVerification(email, redirectTo, captchaToken) {
      const { error } = await db.auth.resend({
        type: "signup",
        email,
        options: { emailRedirectTo: redirectTo, ...withCaptcha(captchaToken) },
      });
      return error ? failed(error) : done;
    },

    async sendPasswordReset(email, redirectTo, captchaToken) {
      const { error } = await db.auth.resetPasswordForEmail(email, {
        redirectTo,
        ...withCaptcha(captchaToken),
      });
      return error ? failed(error) : done;
    },

    async setPassword(password) {
      const refused = await refusedAsPwned(pwned, password);
      if (refused) return refused;
      const { error } = await db.auth.updateUser({ password });
      return error ? failed(error) : done;
    },

    async signOut() {
      // This device only: the user's other devices keep their sessions.
      const { error } = await db.auth.signOut({ scope: "local" });
      return error ? failed(error) : done;
    },

    async deleteAccount() {
      const { error } = await db.rpc("delete_account");
      if (error) return failed({ code: error.code, message: error.message });
      // The user is gone, so the server has no session to end; drop this device's copy.
      await db.auth.signOut({ scope: "local" });
      return done;
    },

    onChange(listener) {
      const { data } = db.auth.onAuthStateChange((event, session) => {
        if (event === "SIGNED_IN") listener("signed-in", userOf(session?.user));
        else if (event === "SIGNED_OUT") listener("signed-out", null);
        else if (event === "PASSWORD_RECOVERY") listener("recovery", userOf(session?.user));
      });
      return () => data.subscription.unsubscribe();
    },
  };
}

/** Where to go after signing in: the `next` a page was given, if it stays on this site.
    Anything that could leave the origin — another host, a scheme, a protocol-relative
    "//host" — goes home instead. */
export function safeNext(next: string | null | undefined, origin: string, home = "/"): string {
  if (!next || !next.startsWith("/") || next.startsWith("//") || next.includes("\\")) return home;
  try {
    const url = new URL(next, origin);
    return url.origin === origin ? url.pathname + url.search + url.hash : home;
  } catch {
    return home;
  }
}

/** The providers a build offers, from its comma-separated setting. Unknown names are dropped. */
export function parseProviders(setting: string | undefined): OAuthProvider[] {
  const asked = (setting ?? "").split(",").map((s) => s.trim().toLowerCase());
  return OAUTH_PROVIDERS.filter((p) => asked.includes(p));
}
